import { useState } from 'react';

import PlaceholderLogo from 'components/common/PlaceholderLogo';
import { getInitials } from 'util/initials';
import resolveMediaUrl from 'util/media';

// Subtle, distinct badge colours so tenants are visually separable in a list.
const BADGE_COLORS = [
  'bg-brand/12 text-brand-dark',
  'bg-success/15 text-success',
  'bg-warning/15 text-warning',
  'bg-[#6D8BFF]/15 text-[#4A63C8]',
  'bg-[#B98BFF]/15 text-[#7A4FC8]',
];

export const initialsOf = (name) => getInitials(name);

const colorOf = (name) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return BADGE_COLORS[h % BADGE_COLORS.length];
};

/**
 * Round badge for a center or organization: its logo when it has one, else the
 * tinted monogram. The colour is what keeps tenants apart in a list, so it stays
 * the fallback rather than the neutral shared Avatar. A logo that fails to load
 * (deleted file, bad URL) falls back to the monogram too.
 *
 * Shared by the header's center switcher and the sidebar's tenant footer.
 *
 * `shape`: 'circle' crops the logo to a round avatar (lists of centers, where
 * uniform round badges read as one column); 'rounded' fits the whole image
 * inside a squircle, which is what a wordmark logo needs to stay legible.
 */
export function TenantBadge({ name, logoUrl, size = 32, shape = 'circle', className = '' }) {
  const [failed, setFailed] = useState(false);
  const resolved = resolveMediaUrl(logoUrl);
  const showImage = resolved && !failed;
  const circle = shape === 'circle';

  return (
    <span
      className={`flex shrink-0 items-center justify-center overflow-hidden font-bold ${
        circle ? 'rounded-full' : 'rounded-[10px]'
      } ${showImage ? 'bg-mist' : colorOf(name)} ${className}`}
      style={{ width: size, height: size }}
    >
      {showImage ? (
        <img
          src={resolved}
          alt=""
          // Squircle logos get breathing room so they read at the same visual
          // size as the Anablr mark they sit beside (that mark's "A" glyph
          // fills well under the full box) — a full-bleed image looks
          // oversized next to it. Circle avatars keep the full-bleed crop.
          className={circle ? 'h-full w-full object-cover' : 'h-[78%] w-[78%] object-contain'}
          onError={() => setFailed(true)}
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}

/**
 * Wide-format tenant logo, for a tenant that picked the 'rectangle' shape
 * (see LogoUploadField) — drawn at its own width instead of cropped into the
 * square badge above, the same way the Anablr wordmark renders at the
 * sidebar's own foot. The sidebar adds the role subtitle beneath it itself
 * (see Sidebar.jsx), so this component still renders just the image.
 *
 * With no logo it falls back to the plain tenant name, so a broken URL never
 * leaves the identity block blank. `placeholder` swaps that fallback for the
 * "ADD YOUR LOGO" mark — for slots that exist to show a logo (a profile
 * header, an upload preview) rather than to identify the tenant, where the
 * name is the more useful thing to keep.
 */
export function TenantWordmark({
  name,
  logoUrl,
  height = 36,
  maxWidth = 176,
  placeholder = false,
  className = '',
}) {
  const [failed, setFailed] = useState(false);
  const resolved = resolveMediaUrl(logoUrl);

  if ((!resolved || failed) && placeholder) {
    return <PlaceholderLogo height={height} maxWidth={maxWidth} className={className} />;
  }

  if (!resolved || failed) {
    return (
      <span
        className={`truncate font-sans text-[15.5px] font-semibold leading-tight tracking-[-0.01em] text-ink ${className}`}
      >
        {name}
      </span>
    );
  }

  return (
    <img
      src={resolved}
      alt={name}
      style={{ height, maxWidth }}
      className={`block w-auto max-w-full shrink-0 object-contain ${className}`}
      onError={() => setFailed(true)}
    />
  );
}

export default TenantBadge;
